import type { ReactElement } from "react";

import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios, { AxiosError } from "axios";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Verification status type
 */
type VerificationStatus = "verifying" | "success" | "error";

/**
 * Verify email response from API
 */
interface VerifyEmailResponse {
  session_token?: string;
  message?: string;
}

/**
 * API error response
 */
interface ApiErrorResponse {
  detail?: string;
}

/**
 * VerifyEmailPage Component
 *
 * Handles email verification from the link sent to users.
 */
const VerifyEmailPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<VerificationStatus>("verifying");
  const [error, setError] = useState<string>("");
  const verificationAttempted = useRef<boolean>(false);

  useEffect(() => {
    const token = searchParams.get("token");

    // Prevent double verification attempts (React 18 Strict Mode / re-renders)
    if (verificationAttempted.current) {
      return;
    }

    if (token) {
      verificationAttempted.current = true;
      verifyEmail(token);
    } else {
      setStatus("error");
      setError("No verification token provided");
    }
  }, [searchParams]);

  const verifyEmail = async (token: string): Promise<void> => {
    try {
      const response = await axios.post<VerifyEmailResponse>(
        `${API}/auth/verify-email`,
        { token },
        { withCredentials: true }
      );

      // Store session token
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }

      setStatus("success");
      toast.success("Email verified successfully!");

      // Redirect to home after 2 seconds
      setTimeout(() => {
        navigate("/home");
      }, 2000);
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      setStatus("error");
      setError(axiosError.response?.data?.detail || "Verification failed");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background:
          "linear-gradient(to bottom, #FFF8F0 0%, #FFE8D6 50%, #FFD4B8 100%)",
      }}
    >
      <div className="w-full max-w-md">
        {/* Card with explicit light theme styling */}
        <div
          className="text-center animate-slide-up"
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "2rem",
            boxShadow: "0 8px 24px rgba(92, 64, 51, 0.12)",
            border: "2px solid #FFE8D6",
          }}
        >
          {status === "verifying" && (
            <>
              <div
                className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full"
                style={{
                  background: "linear-gradient(135deg, #FF8C61 0%, #FF9F7D 100%)",
                }}
              >
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              </div>
              <h1
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "#5C4033" }}
              >
                Verifying your email...
              </h1>
              <p
                style={{ fontFamily: "Quicksand, sans-serif", color: "#8B6F47" }}
              >
                Please wait a moment
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div
                className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full"
                style={{
                  background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                }}
              >
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h1
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "#5C4033" }}
              >
                Email Verified!
              </h1>
              <p
                style={{ fontFamily: "Quicksand, sans-serif", color: "#8B6F47" }}
              >
                Your account is now active. Redirecting you to home...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <div
                className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full"
                style={{
                  background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
                }}
              >
                <XCircle className="w-10 h-10 text-white" />
              </div>
              <h1
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "#5C4033" }}
              >
                Verification Failed
              </h1>
              <p
                className="mb-6"
                style={{ fontFamily: "Quicksand, sans-serif", color: "#8B6F47" }}
              >
                {error}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105 active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #FF8C61 0%, #FF9F7D 100%)",
                  color: "#FFF8F0",
                  fontFamily: "Quicksand, sans-serif"
                }}
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
